import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { localDateStr } from '../../../lib/date';

function todayStr() {
  return localDateStr();
}

function fmtDate(d) {
  return new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });
}

const emptyForm = { date_from: todayStr(), date_to: todayStr(), reason: 'Libur', reopen_note: '', is_active: true };

export default function HariLibur() {
  const [holidays, setHolidays] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tableMissing, setTableMissing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { load(); }, []);

  async function load() {
    const { data, error: err } = await supabase.from('store_holidays').select('*').order('date_from', { ascending: false });
    if (err) {
      console.error(err);
      if (err.code === '42P01' || /relation .* does not exist/i.test(err.message || '')) setTableMissing(true);
      return;
    }
    setTableMissing(false);
    setHolidays(data || []);
  }

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  function resetForm() { setForm(emptyForm); setEditId(null); }

  async function handleSave() {
    if (!form.date_from || !form.date_to) return;
    if (form.date_to < form.date_from) { alert('Tanggal selesai tidak boleh sebelum tanggal mulai'); return; }
    setLoading(true);
    setError('');
    const payload = {
      date_from: form.date_from,
      date_to: form.date_to,
      reason: (form.reason || 'Libur').trim() || 'Libur',
      reopen_note: (form.reopen_note || '').trim(),
      is_active: !!form.is_active,
    };
    const { error: err } = editId
      ? await supabase.from('store_holidays').update(payload).eq('id', editId)
      : await supabase.from('store_holidays').insert(payload);
    setLoading(false);
    if (err) {
      console.error(err);
      if (err.code === '42P01' || /relation .* does not exist/i.test(err.message || '')) {
        setError('Tabel "store_holidays" belum ada di database. Jalankan dulu file migration_add_store_holidays.sql di Supabase SQL Editor, lalu coba lagi.');
      } else {
        setError('Gagal menyimpan: ' + (err.message || 'terjadi kesalahan'));
      }
      return;
    }
    resetForm();
    load();
  }

  async function handleDelete(id) {
    if (!window.confirm('Hapus jadwal libur ini?')) return;
    await supabase.from('store_holidays').delete().eq('id', id);
    load();
  }

  async function toggleActive(h) {
    await supabase.from('store_holidays').update({ is_active: !h.is_active }).eq('id', h.id);
    load();
  }

  function editRow(h) {
    setEditId(h.id);
    setForm({ date_from: h.date_from, date_to: h.date_to, reason: h.reason, reopen_note: h.reopen_note || '', is_active: h.is_active });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const today = todayStr();
  const isCurrentlyOn = (h) => h.is_active && h.date_from <= today && h.date_to >= today;

  return (
    <div style={{ padding: 'clamp(16px,4vw,28px) clamp(12px,4vw,24px) 60px', maxWidth: 640 }}>
      <div style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '1.5rem', marginBottom: 4 }}>Hari Libur</div>
      <div style={{ fontSize: '.82rem', color: 'var(--muted)', marginBottom: 24 }}>
        Saat rentang tanggal ini aktif, web akan menampilkan halaman "Sedang Libur" dan menolak order baru — baik menu siap-makan maupun PO.
      </div>

      {tableMissing && (
        <div style={{ background: '#FDEBE5', border: '1px solid rgba(205,82,38,.3)', borderRadius: 10, padding: '14px 16px', marginBottom: 18, fontSize: '.82rem', color: '#B23A15' }}>
          ⚠️ Tabel <code>store_holidays</code> belum ditemukan di database. Buka Supabase → SQL Editor, jalankan file <code>migration_add_store_holidays.sql</code> yang sudah dikirim sebelumnya, lalu refresh halaman ini.
        </div>
      )}

      <div style={{ background: 'white', borderRadius: 'var(--radius)', border: '1px solid var(--border)', padding: '18px 20px', marginBottom: 18, boxShadow: 'var(--shadow)' }}>
        <div style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '1rem', marginBottom: 14 }}>{editId ? 'Edit Jadwal Libur' : 'Tambah Jadwal Libur'}</div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
          <div>
            <label style={{ display: 'block', fontSize: '.74rem', color: 'var(--muted)', marginBottom: 5 }}>Dari Tanggal</label>
            <input type="date" value={form.date_from} onChange={e => set('date_from', e.target.value)}
              style={{ width: '100%', border: '1.5px solid #DDD', borderRadius: 8, padding: '8px 12px', fontSize: '.86rem', outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '.74rem', color: 'var(--muted)', marginBottom: 5 }}>Sampai Tanggal</label>
            <input type="date" value={form.date_to} onChange={e => set('date_to', e.target.value)}
              style={{ width: '100%', border: '1.5px solid #DDD', borderRadius: 8, padding: '8px 12px', fontSize: '.86rem', outline: 'none', boxSizing: 'border-box' }} />
          </div>
        </div>

        <div style={{ marginBottom: 10 }}>
          <label style={{ display: 'block', fontSize: '.74rem', color: 'var(--muted)', marginBottom: 5 }}>Alasan (tampil ke customer)</label>
          <input type="text" value={form.reason} onChange={e => set('reason', e.target.value)} placeholder="Contoh: Libur Lebaran, Cuti Bersama, dsb"
            style={{ width: '100%', border: '1.5px solid #DDD', borderRadius: 8, padding: '8px 12px', fontSize: '.86rem', outline: 'none', boxSizing: 'border-box' }} />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: '.74rem', color: 'var(--muted)', marginBottom: 5 }}>
            Info Buka Lagi <span style={{ color: '#AAA' }}>(opsional — kalau kosong, otomatis "Kami buka lagi mulai [tanggal setelah Sampai Tanggal]")</span>
          </label>
          <input type="text" value={form.reopen_note} onChange={e => set('reopen_note', e.target.value)} placeholder="Contoh: Buka lagi Senin, 12 Agustus 2026 pukul 08.00"
            style={{ width: '100%', border: '1.5px solid #DDD', borderRadius: 8, padding: '8px 12px', fontSize: '.86rem', outline: 'none', boxSizing: 'border-box' }} />
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, cursor: 'pointer' }}>
          <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} />
          <span style={{ fontSize: '.82rem' }}>Aktifkan jadwal ini</span>
        </label>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button onClick={handleSave} disabled={loading} style={{ padding: '9px 18px', borderRadius: 8, background: '#CD5226', color: '#fff', border: 'none', fontSize: '.82rem', fontWeight: 600, fontFamily: "'DM Sans',sans-serif", cursor: loading ? 'default' : 'pointer' }}>
            {loading ? 'Menyimpan...' : (editId ? 'Simpan Perubahan' : '+ Tambah Jadwal')}
          </button>
          {editId && <button onClick={resetForm} style={{ padding: '9px 14px', borderRadius: 8, background: 'var(--cream)', border: '1px solid var(--border)', fontSize: '.82rem', fontFamily: "'DM Sans',sans-serif", color: 'var(--muted)' }}>Batal</button>}
        </div>
        {error && (
          <div style={{ marginTop: 12, fontSize: '.78rem', color: '#B23A15', background: '#FDEBE5', borderRadius: 8, padding: '9px 13px' }}>
            {error}
          </div>
        )}
      </div>

      <div style={{ background: 'white', borderRadius: 'var(--radius)', border: '1px solid var(--border)', overflow: 'hidden', boxShadow: 'var(--shadow)' }}>
        {holidays.length === 0 ? (
          <div style={{ padding: '28px', textAlign: 'center', color: 'var(--muted)', fontSize: '.84rem' }}>Belum ada jadwal libur</div>
        ) : holidays.map((h, i) => {
          const on = isCurrentlyOn(h);
          return (
            <div key={h.id} style={{ padding: '14px 18px', borderBottom: i < holidays.length - 1 ? '1px solid var(--border)' : 'none' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 6 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                    <span style={{ fontSize: '.9rem', fontWeight: 600 }}>{h.reason}</span>
                    {on && (
                      <span style={{ fontSize: '.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px', color: '#B23A15', background: '#FDEBE5', padding: '2px 8px', borderRadius: 999 }}>
                        Sedang Berlangsung
                      </span>
                    )}
                    {!h.is_active && (
                      <span style={{ fontSize: '.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px', color: 'var(--muted)', background: 'var(--cream)', padding: '2px 8px', borderRadius: 999 }}>
                        Nonaktif
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '.78rem', color: 'var(--muted)' }}>
                    {h.date_from === h.date_to ? fmtDate(h.date_from) : `${fmtDate(h.date_from)} – ${fmtDate(h.date_to)}`}
                  </div>
                  {h.reopen_note && <div style={{ fontSize: '.76rem', color: 'var(--muted)', marginTop: 2, fontStyle: 'italic' }}>"{h.reopen_note}"</div>}
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button onClick={() => toggleActive(h)} style={{ padding: '4px 10px', borderRadius: 6, background: 'var(--cream)', border: '1px solid var(--border)', fontSize: '.72rem' }}>
                    {h.is_active ? 'Nonaktifkan' : 'Aktifkan'}
                  </button>
                  <button onClick={() => editRow(h)} style={{ padding: '4px 10px', borderRadius: 6, background: 'var(--cream)', border: '1px solid var(--border)', fontSize: '.72rem' }}>Edit</button>
                  <button onClick={() => handleDelete(h.id)} style={{ padding: '4px 10px', borderRadius: 6, background: '#FFEBEE', border: '1px solid #FFCDD2', color: '#C62828', fontSize: '.72rem' }}>Hapus</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
